import type { TokenView } from '@onuw/protocol'
import { ARTIFACT_IDS, type ArtifactId, MARK_IDS, type MarkId, type TokenImageId } from '@onuw/roles-data'
import { useTranslation } from 'react-i18next'
import { tokenImage } from '../lib/assets'
import { cx } from './Card'
import styles from './Token.module.css'
import { useTooltip } from './Tooltip'

const isArtifact = (id: string): id is ArtifactId => (ARTIFACT_IDS as readonly string[]).includes(id)
const isMark = (id: string): id is MarkId => (MARK_IDS as readonly string[]).includes(id)

/**
 * What a token is, as far as this player may know: its art (none for seat numbers) and its strings,
 * `<key>.title` and `<key>.description`. An artifact or mark nobody showed you is only its back.
 */
export const tokenInfo = (
  tk: TokenView,
): { image: TokenImageId | null; key: string; params?: { number: number } } => {
  switch (tk.kind) {
    case 'shield':
      return { image: 'shield', key: 'tokens:shield' }
    case 'artifact':
      return tk.identity && isArtifact(tk.identity)
        ? { image: tk.identity, key: `tokens:${tk.identity}` }
        : { image: 'artifactBack', key: 'tokens:artifact' }
    case 'mark':
      return tk.identity && isMark(tk.identity)
        ? { image: tk.identity, key: `tokens:${tk.identity}` }
        : { image: 'markBack', key: 'tokens:mark' }
    case 'number':
      return { image: null, key: 'tokens:number', params: { number: tk.number ?? tk.seat + 1 } }
  }
}

/** A token's name and what it does, translated. */
export function useTokenText() {
  const { t } = useTranslation()
  return (tk: TokenView) => {
    const { key, params = {} } = tokenInfo(tk)
    return { title: t(`${key}.title`, params), description: t(`${key}.description`, params) }
  }
}

/** The round face of a token: its medallion art, or a gold coin with the seat number. */
function TokenFace({
  token,
  sizes,
  className,
}: {
  token: TokenView
  sizes: string
  className?: string | undefined
}) {
  const { image } = tokenInfo(token)
  const art = image ? tokenImage(image) : null
  if (!art?.available)
    return (
      <span className={cx(styles.face, styles.coin, className)}>
        <span>{token.kind === 'number' ? token.number : '✦'}</span>
      </span>
    )
  return (
    <picture className={cx(styles.face, className)}>
      <source type="image/avif" srcSet={art.avifSet} sizes={sizes} />
      <source type="image/webp" srcSet={art.webpSet} sizes={sizes} />
      <img src={art.src} alt="" sizes={sizes} decoding="async" draggable={false} />
    </picture>
  )
}

/** The medallion beside the token's name and what it does: the tooltip, and a mark shown in a prompt. */
export function TokenDetails({ token }: { token: TokenView }) {
  const { title, description } = useTokenText()(token)
  return (
    <div className={styles.tip}>
      <TokenFace token={token} sizes="56px" className={styles.tipFace} />
      <div>
        <strong className={styles.tipTitle}>{title}</strong>
        <span className={styles.tipText}>{description}</span>
      </div>
    </div>
  )
}

/** A token lying on a seat's card. Hover (or tap) shows what it is and what it does. */
export function SeatToken({ token }: { token: TokenView }) {
  const { title } = useTokenText()(token)
  const { ref, handlers } = useTooltip<HTMLSpanElement>(() => <TokenDetails token={token} />)
  return (
    <span
      ref={ref}
      role="img"
      aria-label={title}
      className={styles.disc}
      data-token={token.kind}
      {...handlers}
    >
      <TokenFace token={token} sizes="32px" />
    </span>
  )
}
